import React, { useState, useEffect } from 'react';
import { getAllStudents } from '../utils/studentCache';
import { supabase } from '../lib/supabase';
import ImportButton from '../components/ui/ImportButton';
import { useAuth } from '../hooks/useAuth';
import useLiveRefresh from '../hooks/useLiveRefresh';
import toast from 'react-hot-toast';
import { Plus, X, Search, ClipboardList, Eye, Edit2, Trash2 } from 'lucide-react';
import SearchableSelect from '../components/ui/SearchableSelect';
import { saveStyledXlsx } from '../lib/excelStyle';
import DateInput from '../components/ui/DateInput';
import { fmtDMY } from '../lib/dateFormat';

// PostgREST returns max 1000 rows per request — page anything that can grow beyond that.
async function fetchAllPaged(buildQuery) {
  let all = [], from = 0;
  while (true) {
    const { data, error } = await buildQuery().range(from, from + 999);
    if (error) throw error;
    all = all.concat(data || []);
    if (!data || data.length < 1000) break;
    from += 1000;
  }
  return all;
}
// Local (IST) date. toISOString() is the UTC date = "yesterday" before 5:30am IST.
const localDate = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const esc = t => String(t == null ? '' : t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const activeStudent = s => s.is_active !== false && !['Discontinued', 'Shifted'].includes(s.student_status);
const waNumber = raw => { const ph = String(raw || '').split('/').pop().replace(/\D/g, ''); return ph.startsWith('91') ? ph : ph.length === 10 ? '91' + ph : ph; };
const ASSIGNMENT_SETUP_SQL = `alter table assignments
  add column if not exists due_date date,
  add column if not exists feedback text,
  add column if not exists submission_link text,
  add column if not exists updated_by text,
  add column if not exists updated_at timestamptz;`;
// Saves with due_date / feedback; if those columns are missing, saves without them and says what to run.
async function writeAssignment(run, payload) {
  let res = await run(payload);
  if (res.error && /column|schema cache/i.test(res.error.message || '')) {
    const bad = ['due_date', 'feedback', 'submission_link'].filter(k => String(res.error.message).includes(k));
    if (bad.length) {
      const p = { ...payload }; bad.forEach(k => delete p[k]);
      res = await run(p);
      if (!res.error) toast.error(`Saved WITHOUT ${bad.join(' / ')} — run this once in Supabase SQL Editor:\n${ASSIGNMENT_SETUP_SQL}`, { duration: 15000 });
    }
  }
  return res;
}


  function getBatchDiscipline(batchName) {
    if (!batchName) return null;
    const b = batchName.toUpperCase();
    if (b.startsWith('EM') || b.startsWith('TM')) return 'mechanical';
    if (b.startsWith('EC') || b.startsWith('TC')) return 'civil';
    return null;
  }
function NotSubmittedPanel({ show, onToggle, filterBatch, filterTopic, assignments, students, hideToggle }) {
  const submitted = new Set(
    assignments
      .filter(a => ((a.students?.batch_name || a.students?.batches?.batch_name) || '') === filterBatch && a.topic_name === filterTopic)
      .map(a => a.student_id)
  );
  const notSub = students.filter(s => s.batch_name === filterBatch && !submitted.has(s.id) && activeStudent(s));
  if (!filterBatch || !filterTopic) return null;
  function printNS() {
    const w = window.open('', '_blank');
    if (!w) { toast.error('Allow pop-ups to print'); return; }
    const rows = notSub.map((s, i) => `<tr><td>${i + 1}</td><td>${esc(s.full_name)}</td><td>${esc(s.register_number)}</td><td>${esc(s.course_name)}</td><td>${esc(s.contact_number)}</td></tr>`).join('');
    w.document.write(`<html><head><title>Not Submitted</title><style>body{font-family:Arial,sans-serif;font-size:12px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #ddd;padding:6px 10px}th{background:#f3f4f6}</style></head><body><h2>Not Submitted - ${esc(filterBatch)} / ${esc(filterTopic)}</h2><p>Total: ${notSub.length}</p><table><thead><tr><th>#</th><th>Name</th><th>Reg No</th><th>Course</th><th>Contact</th></tr></thead><tbody>${rows}</tbody></table></body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 300);
  }
  function exportNS() {
    if (!notSub.length) { toast.error('Nothing to export'); return; }
    saveStyledXlsx(`Not_submitted_${filterBatch}_${filterTopic}.xlsx`, [{ name: 'Not submitted', title: `Not Submitted — ${filterBatch} / ${filterTopic}`, rows: notSub.map((s, i) => ({ 'S.No': i + 1, Name: s.full_name, 'Reg No': s.register_number, Course: s.course_name || '', Contact: s.contact_number || '' })) }]);
  }
  function remindOne(s) {
    const ph = waNumber(s.whatsapp_number || s.contact_number);
    if (!ph) { toast.error('No phone number'); return; }
    const msg = `Dear ${s.full_name},\nYour assignment for *${filterTopic}* (${filterBatch}) has not been submitted yet. Please submit it at the earliest.\n— Armstech Engineers`;
    window.open(`whatsapp://send?phone=${ph}&text=${encodeURIComponent(msg)}`, '_self');
  }
  return (
    <div style={{marginBottom:12,border:'1px solid #e5e7eb',borderRadius:8,overflow:'hidden'}}>
      <div style={{background:'#fef3c7',padding:'8px 14px',cursor:'pointer',display:'flex',justifyContent:'space-between',alignItems:'center'}} onClick={onToggle}>
        <span style={{fontWeight:600,color:'#92400e'}}>Not Submitted — {filterBatch} / {filterTopic} ({notSub.length} students)</span>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          {notSub.length>0&&(
            <button className="btn btn-ghost btn-sm" style={{fontSize:11,color:'#25d366'}}
              onClick={()=>{
                const names = notSub.map(s=>`• ${s.full_name} (${s.register_number})`).join('\n');
                const msg = `*Assignment Reminder — Armstech*\n`+
                  `Batch: ${filterBatch}\nTopic: ${filterTopic}\n\n`+
                  `The following students have not submitted:\n${names}\n\n`+
                  `Please submit your assignment at the earliest.`;
                navigator.clipboard.writeText(msg).then(()=>toast.success('Message copied!'));
              }}>
              💬 Copy Reminder
            </button>
          )}
          <button className="btn btn-sm" style={{fontSize:11,padding:'2px 10px'}} onClick={e=>{e.stopPropagation();exportNS();}}>⬇ Excel</button>
          <button className="btn btn-sm" style={{fontSize:11,padding:'2px 10px'}} onClick={e=>{e.stopPropagation();printNS();}}>🖨️ Print</button>
          {!hideToggle && <span>{show ? 'Hide' : 'Show'}</span>}
        </div>
      </div>
      {show && (
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <thead>
            <tr style={{background:'#f9fafb'}}>
              <th style={{padding:'6px 12px',textAlign:'left',borderBottom:'1px solid #e5e7eb'}}>Student</th>
              <th style={{padding:'6px 12px',textAlign:'left',borderBottom:'1px solid #e5e7eb'}}>Reg No</th>
              <th style={{padding:'6px 12px',textAlign:'left',borderBottom:'1px solid #e5e7eb'}}>Course</th>
              <th style={{padding:'6px 12px',textAlign:'left',borderBottom:'1px solid #e5e7eb'}}>Contact</th>
              <th style={{padding:'6px 12px',textAlign:'left',borderBottom:'1px solid #e5e7eb'}}></th>
            </tr>
          </thead>
          <tbody>
            {notSub.length === 0
              ? <tr><td colSpan={5} style={{padding:'12px',color:'#16a34a'}}>All students submitted</td></tr>
              : notSub.map((s, i) => (
                <tr key={s.id} style={{background:i%2?'#f9fafb':'#fff'}}>
                  <td style={{padding:'6px 12px',borderBottom:'1px solid #f3f4f6'}}>{s.full_name}</td>
                  <td style={{padding:'6px 12px',borderBottom:'1px solid #f3f4f6'}}>{s.register_number}</td>
                  <td style={{padding:'6px 12px',borderBottom:'1px solid #f3f4f6'}}>{s.course_name||'—'}</td>
                  <td style={{padding:'6px 12px',borderBottom:'1px solid #f3f4f6'}}>{s.contact_number||'—'}</td>
                  <td style={{padding:'6px 12px',borderBottom:'1px solid #f3f4f6'}}><button className="btn btn-ghost btn-sm" style={{fontSize:11,color:'#25d366'}} onClick={()=>remindOne(s)}>💬</button></td>
                </tr>
              ))
            }
          </tbody>
        </table>
      )}
    </div>
  );
}


function sortBatches(batches) {
  // Ongoing first, then Upcoming, then Completed; inside each group the newest batch (latest start date) comes first.
  const rank = s => { const v = String(s || '').trim().toLowerCase(); return (v === 'ongoing' || v === 'active' || v === 'running') ? 0 : (v === 'completed' || v === 'closed') ? 2 : 1; };
  const sorted = [...(batches || [])].sort((a, b) => {
    const d = rank(a.status) - rank(b.status);
    if (d) return d;
    const sa = a.start_date || '', sb = b.start_date || '';
    if (sa !== sb) return sa < sb ? 1 : -1;
    return (b.batch_name || '').localeCompare(a.batch_name || '', undefined, { numeric: true });
  });
  try { window.__batchRank = new Map(sorted.map((b, i) => [b.batch_name, i])); } catch (e) {}
  return sorted;
}
// Order plain batch names the same way as the batch dropdowns (ongoing first, newest first); unknown names go last.
function orderBatchNames(names) {
  const r = window.__batchRank;
  return [...names].sort((a, b) => {
    const pa = r && r.has(a) ? r.get(a) : 1e9, pb = r && r.has(b) ? r.get(b) : 1e9;
    if (pa !== pb) return pa - pb;
    return String(b).localeCompare(String(a), undefined, { numeric: true });
  });
}

// Sort students by register number the natural way (TM23002 before TM23010).
const byRegNo = (a, b) => String(a.register_number || '').localeCompare(String(b.register_number || ''), undefined, { numeric: true });

const OWNER_MAIL = 'ershadarmstech@gmail.com';
const OFFICE_MAILS = ['armstechac@gmail.com', 'armstechekm@gmail.com'];

export default function Assignments() {
  const { isEditor, canEdit, canEditBatch, assignedBatches, isCoordinator, isAdmin, staff, user } = useAuth();
  // staff assigned to specific batches can change only those batches; every other batch is view-only
  const meMail = String(user?.email || '').trim().toLowerCase();
  const batchRestricted = meMail !== OWNER_MAIL && !OFFICE_MAILS.includes(meMail) && !isAdmin && ((assignedBatches || []).length > 0 || !!isCoordinator);
  const [assignments, setAssignments] = useState([]);
  const [students, setStudents] = useState([]);
  const [facultyList, setFacultyList] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [filterBatch, setFilterBatch] = useState('');
  const [filterTopic, setFilterTopic] = useState('');
  const [showReport, setShowReport] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterFaculty, setFilterFaculty] = useState('');
  const [saving, setSaving] = useState(false);
  const [batches, setBatches] = useState([]);
  const ownBatch = name => !batchRestricted || (typeof canEditBatch === 'function' && batches.some(b => b.batch_name === name && canEditBatch(b.id)));
  const canEditA = canEdit('assignments') || batchRestricted;   // an assigned batch grants edit on that batch even if the section is View
  const canEditRow = name => canEditA && ownBatch(name);
  const editableBatches = batches.filter(b => ownBatch(b.batch_name));
  const NOT_OWN = 'You can change only your own batches';
  const [filterResub, setFilterResub] = useState(false);
  const [filterOverdue, setFilterOverdue] = useState(false);
  const [bulkScores, setBulkScores] = useState({});
  const [bulkMarkCorrected, setBulkMarkCorrected] = useState(true);
  const [bulkEditDue, setBulkEditDue] = useState('');

  const emptyForm = {
    student_id: '', topic_name: '', assignment_title: '',
    submitted_date: '', corrected: 'pending', returned_date: '',
    score: '', faculty_remarks: '', resubmission_required: false,
    resubmission_date: '', faculty_id: '', due_date: '', feedback: '', submission_link: ''
  };
  const [form, setForm] = useState(emptyForm);
  const [modalBatch, setModalBatch] = useState('');
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkBatch, setBulkBatch] = useState('');
  const [bulkForm, setBulkForm] = useState({ topic_name:'', assignment_title:'', submitted_date:'', corrected:'pending', returned_date:'', score:'', faculty_remarks:'', resubmission_required:false, resubmission_date:'', faculty_id:'', due_date:'' });
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkSelectedStudents, setBulkSelectedStudents] = useState([]);
  const [bulkEditSearch, setBulkEditSearch] = useState('');
  const [bulkStudentSearch, setBulkStudentSearch] = useState('');
  const [showNotSubmitted, setShowNotSubmitted] = useState(false);
  const [activeTab, setActiveTab] = useState('assignments');
  const [nsFilterBatch, setNsFilterBatch] = useState('');
  const [nsFilterTopic, setNsFilterTopic] = useState('');
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkEditBatch, setBulkEditBatch] = useState('');
  const [bulkEditTopic, setBulkEditTopic] = useState('');
  const [bulkEditSelected, setBulkEditSelected] = useState([]);
  const [bulkEditAction, setBulkEditAction] = useState('status');
  const [bulkEditStatus, setBulkEditStatus] = useState('yes');
  const [bulkEditFaculty, setBulkEditFaculty] = useState('');
  const [bulkEditSaving, setBulkEditSaving] = useState(false);

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    try {
      const [a, f, t, b, st] = await Promise.all([
        fetchAllPaged(() => supabase.from('assignments')
          .select('*, students(full_name, register_number, batch_name, course_name), faculties(faculty_name)')
          .order('created_at', { ascending: false }).order('id')),
        supabase.from('faculties').select('id, faculty_name').order('faculty_name'),
        supabase.from('topics').select('id, topic_name, short_code, discipline').order('topic_name'),
        supabase.from('batches').select('id, batch_name, status, start_date').order('batch_name'),
        getAllStudents(),
      ]);
      setAssignments(a || []);
      setFacultyList(f.data || []);
      setTopics(t.data || []);
      setBatches(sortBatches(b.data || []));
      setStudents(Array.isArray(st) ? st : (st?.data || []));
    } catch (e) { toast.error('Could not load assignments: ' + e.message); }
    setLoading(false);
  }

  // another staff member added / changed an assignment: reload just the assignments list, quietly
  useLiveRefresh(['assignments'], async () => {
    try {
      const a = await fetchAllPaged(() => supabase.from('assignments')
        .select('*, students(full_name, register_number, batch_name, course_name), faculties(faculty_name)')
        .order('created_at', { ascending: false }).order('id'));
      setAssignments(a || []);
    } catch (e) { /* quiet */ }
  }, true, 1500);

  const editorName = () => staff?.full_name || staff?.staff_name || user?.email;
  function openAdd() { setEditing(null); setForm(emptyForm); setModalBatch(''); setShowModal(true); }
  function closeModal() { setShowModal(false); setEditing(null); setForm(emptyForm); setModalBatch(''); }

  async function save() {
    { const stu = students.find(x => x.id === form.student_id); if (!canEditRow(stu?.batch_name)) { toast.error(NOT_OWN); return; } }
    if (!form.student_id || !form.topic_name) { toast.error('Select student and topic'); return; }
    if (form.submitted_date && form.returned_date && form.returned_date < form.submitted_date) { toast.error('Returned date cannot be before the submitted date'); return; }
    if (form.score !== '' && form.score != null && !(Number(form.score) >= 0)) { toast.error('Score must be a number ≥ 0'); return; }
    if (!editing) {
      const dup = assignments.find(a => a.student_id === form.student_id && a.topic_name === form.topic_name && (a.assignment_title || '') === (form.assignment_title || ''));
      if (dup && !window.confirm('This student already has an assignment with the same topic and title. Add another one?')) return;
    }
    setSaving(true);
    const payload = {
      updated_by: editorName(), updated_at: new Date().toISOString(),
      student_id:            form.student_id,
      topic_name:            form.topic_name,
      assignment_title:      form.assignment_title || null,
      due_date:              form.due_date || null,
      submitted_date:        form.submitted_date || null,
      corrected:             form.corrected,
      returned_date:         form.returned_date || null,
      score:                 form.score !== '' && form.score != null ? Number(form.score) : null,
      faculty_remarks:       form.faculty_remarks || null,
      feedback:              form.feedback || null,
      submission_link:       form.submission_link?.trim() || null,
      resubmission_required: form.resubmission_required,
      resubmission_date:     form.resubmission_required ? (form.resubmission_date || null) : null,
      faculty_id:            form.faculty_id || null,
    };
    const { error } = await writeAssignment(
      p => editing ? supabase.from('assignments').update(p).eq('id', editing) : supabase.from('assignments').insert(p), payload);
    if (error) toast.error(error.message);
    else {
      toast.success(editing ? 'Assignment updated' : 'Assignment recorded');
      closeModal();
      loadAll();
    }
    setSaving(false);
  }

  async function saveBulkAssignments() {
    if (!canEditRow(bulkBatch)) { toast.error(NOT_OWN); return; }
    if (!bulkBatch) { toast.error('Select a batch'); return; }
    if (!bulkForm.topic_name) { toast.error('Select a topic'); return; }
    if (!bulkSelectedStudents.length) { toast.error('Select at least one student'); return; }
    if (bulkForm.score !== '' && !(Number(bulkForm.score) >= 0)) { toast.error('Score must be a number ≥ 0'); return; }
    // Skip students who already have this topic + title (so pressing Save twice cannot create duplicates)
    const title = bulkForm.assignment_title || null;
    const already = new Set(assignments.filter(a => a.topic_name === bulkForm.topic_name && (a.assignment_title || null) === title).map(a => a.student_id));
    const ids = bulkSelectedStudents.filter(id => !already.has(id));
    const skipped = bulkSelectedStudents.length - ids.length;
    if (!ids.length) { toast.error('All selected students already have this assignment'); return; }
    setBulkSaving(true);
    const rows = ids.map(sid => ({
      student_id: sid,
      topic_name: bulkForm.topic_name || null,
      assignment_title: title,
      due_date: bulkForm.due_date || null,
      submitted_date: bulkForm.submitted_date || null,
      corrected: bulkForm.corrected || 'pending',
      returned_date: bulkForm.returned_date || null,
      score: bulkForm.score !== '' ? Number(bulkForm.score) : null,
      faculty_remarks: bulkForm.faculty_remarks || null,
      resubmission_required: bulkForm.resubmission_required || false,
      resubmission_date: bulkForm.resubmission_required ? (bulkForm.resubmission_date || null) : null,
      faculty_id: bulkForm.faculty_id || null,
      updated_by: editorName(), updated_at: new Date().toISOString(),
    }));
    let error = null;
    for (let i = 0; i < rows.length && !error; i += 100) {
      const r = await writeAssignment(p => supabase.from('assignments').insert(p), rows.slice(i, i + 100).map(x => x));
      error = r.error;
    }
    if (error) toast.error(error.message);
    else {
      toast.success(`Assignment added for ${rows.length} students${skipped ? ` (${skipped} already had it)` : ''}`);
      setShowBulkModal(false);
      setBulkBatch('');
      setBulkSelectedStudents([]);
      setBulkForm({ topic_name:'', assignment_title:'', submitted_date:'', corrected:'pending', returned_date:'', score:'', faculty_remarks:'', resubmission_required:false, resubmission_date:'', faculty_id:'', due_date:'' });
      loadAll();
    }
    setBulkSaving(false);
  }

  function openEdit(a) {
    if (!canEditRow(a.students?.batch_name)) { toast.error(NOT_OWN); return; }
    setEditing(a.id);
    const editStu = students.find(s=>s.id===a.student_id);
    setModalBatch(editStu?.batch_name || a.students?.batch_name || '');
    setForm({
      student_id:            a.student_id || '',
      topic_name:            a.topic_name || '',
      assignment_title:      a.assignment_title || '',
      submitted_date:        a.submitted_date || '',
      corrected:             a.corrected || 'pending',
      returned_date:         a.returned_date || '',
      score:                 a.score ?? '',
      due_date:              a.due_date || '',
      feedback:              a.feedback || '',
      submission_link:       a.submission_link || '',
      faculty_remarks:       a.faculty_remarks || '',
      resubmission_required: a.resubmission_required || false,
      resubmission_date:     a.resubmission_date || '',
      faculty_id:            a.faculty_id || '',
    });
    setShowModal(true);
  }

  async function deleteAssignment(id) {
    { const row = assignments.find(x => x.id === id); if (!canEditRow(row?.students?.batch_name)) { toast.error(NOT_OWN); return; } }
    if (!window.confirm('Delete this assignment? This cannot be undone.')) return;
    const { error } = await supabase.from('assignments').delete().eq('id', id);
    if (error) toast.error(error.message);
    else { toast.success('Assignment deleted'); loadAll(); }
  }

  async function updateCorrected(id, corrected) {
    { const row = assignments.find(x => x.id === id); if (!canEditRow(row?.students?.batch_name)) { toast.error(NOT_OWN); return; } }
    const patch = { corrected, returned_date: corrected === 'yes' ? localDate() : null, updated_by: editorName(), updated_at: new Date().toISOString() };
    const { error } = await supabase.from('assignments').update(patch).eq('id', id);
    if (error) toast.error(error.message);
    else { toast.success('Marked corrected'); setAssignments(prev => prev.map(x => x.id === id ? { ...x, ...patch } : x)); }
  }

  const filtered = assignments.filter(a => {
    const q = search.toLowerCase();
    const matchSearch = !q || a.students?.full_name?.toLowerCase().includes(q) || a.topic_name?.toLowerCase().includes(q) || a.students?.register_number?.toLowerCase().includes(q);
    const matchStatus  = !filterStatus  || a.corrected === filterStatus;
    const matchBatch   = !filterBatch   || (a.students?.batches?.batch_name || a.students?.batch_name) === filterBatch;
    const matchTopic   = !filterTopic   || a.topic_name === filterTopic;
    const matchFaculty = !filterFaculty || a.faculty_id === filterFaculty || a.faculties?.faculty_name === filterFaculty;
    const matchResub   = !filterResub || a.resubmission_required;
    const matchOverdue = !filterOverdue || (a.due_date && a.corrected === 'pending' && a.due_date < localDate());
    return matchSearch && matchStatus && matchBatch && matchTopic && matchFaculty && matchResub && matchOverdue;
  });

  function remindAssignment(a) {
    const st = students.find(x => x.id === a.student_id);
    const ph = waNumber(st?.whatsapp_number || st?.contact_number);
    if (!ph) { toast.error('No phone number for this student'); return; }
    const due = a.due_date ? ` (due ${a.due_date})` : '';
    const msg = `Dear ${a.students?.full_name || 'Student'},\nYour assignment *${a.topic_name}${a.assignment_title ? ' - ' + a.assignment_title : ''}*${due} is pending. Please submit it at the earliest.\n— Armstech Engineers`;
    window.open(`whatsapp://send?phone=${ph}&text=${encodeURIComponent(msg)}`, '_self');
  }

  // Student-wise history (all assignments of one student) + export
  function studentHistory(sid) { return assignments.filter(x => x.student_id === sid).sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')); }
  function exportStudentHistory(sid) {
    const list = studentHistory(sid);
    if (!list.length) return;
    saveStyledXlsx(`Assignments_${(list[0].students?.register_number || 'student')}.xlsx`, [{ name: 'History', title: `Assignment History — ${list[0].students?.full_name || ''} (${list[0].students?.register_number || ''})`, rows: list.map((a, i) => ({ 'S.No': i + 1, Topic: a.topic_name || '', Title: a.assignment_title || '', 'Due Date': a.due_date || '', Submitted: a.submitted_date || '', Status: a.corrected, Score: a.score ?? '', Returned: a.returned_date || '', Remarks: a.faculty_remarks || '', Feedback: a.feedback || '' })) }]);
  }

  function exportList() {
    if (!filtered.length) { toast.error('Nothing to export'); return; }
    const rows = filtered.map((a, i) => ({
      'S.No': i + 1, Student: a.students?.full_name || '', 'Reg No': a.students?.register_number || '', Batch: a.students?.batch_name || '',
      Topic: a.topic_name || '', Title: a.assignment_title || '', 'Due Date': a.due_date || '', Submitted: a.submitted_date || '',
      Status: a.corrected, Returned: a.returned_date || '', Score: a.score ?? '', 'Resubmission': a.resubmission_required ? 'Yes' : 'No',
      Faculty: a.faculties?.faculty_name || facultyList.find(f => f.id === a.faculty_id)?.faculty_name || '', Remarks: a.faculty_remarks || '',
    }));
    saveStyledXlsx(`Assignments_${localDate()}.xlsx`, [{ name: 'Assignments', title: 'Assignments Report — Armstech Engineers', rows }]);
  }

  async function applyBulkEdit() {
    { const ids = bulkEditAction === 'scores' ? Object.keys(bulkScores) : bulkEditSelected; if (ids.some(id => !canEditRow(assignments.find(x => String(x.id) === String(id))?.students?.batch_name))) { toast.error(NOT_OWN); return; } }
    const by = { updated_by: editorName(), updated_at: new Date().toISOString() };
    setBulkEditSaving(true);
    try {
      if (bulkEditAction === 'scores') {
        const entries = Object.entries(bulkScores).filter(([, v]) => v !== '' && v != null);
        if (!entries.length) { toast.error('Enter at least one score'); setBulkEditSaving(false); return; }
        if (entries.some(([, v]) => !(Number(v) >= 0))) { toast.error('Scores must be numbers ≥ 0'); setBulkEditSaving(false); return; }
        const res = await Promise.all(entries.map(([id, v]) => supabase.from('assignments')
          .update({ score: Number(v), ...(bulkMarkCorrected ? { corrected: 'yes', returned_date: localDate() } : {}), ...by }).eq('id', id)));
        const bad = res.filter(r => r.error);
        if (bad.length) toast.error(`${bad.length} score(s) failed: ${bad[0].error.message}`);
        else toast.success(`Saved ${entries.length} scores`);
        setBulkScores({});
      } else {
        let obj;
        if (bulkEditAction === 'status') obj = { corrected: bulkEditStatus, ...(bulkEditStatus === 'yes' ? { returned_date: localDate() } : {}) };
        else if (bulkEditAction === 'faculty') { if (!bulkEditFaculty) { toast.error('Select a faculty'); setBulkEditSaving(false); return; } obj = { faculty_id: bulkEditFaculty }; }
        else if (bulkEditAction === 'submitted') obj = { submitted_date: localDate() };   // status is NOT changed
        else if (bulkEditAction === 'due') { if (!bulkEditDue) { toast.error('Pick a due date'); setBulkEditSaving(false); return; } obj = { due_date: bulkEditDue }; }
        let failed = 0;
        for (let i = 0; i < bulkEditSelected.length; i += 100) {
          const chunk = bulkEditSelected.slice(i, i + 100);
          const r = await writeAssignment(p => supabase.from('assignments').update(p).in('id', chunk), { ...obj, ...by });
          if (r.error) { failed += chunk.length; toast.error(r.error.message); }
        }
        if (failed < bulkEditSelected.length) toast.success(`Updated ${bulkEditSelected.length - failed} assignments`);
        setBulkEditSelected([]);
      }
      await loadAll();
    } catch (e) { toast.error('Bulk update failed: ' + e.message); }
    setBulkEditSaving(false);
  }

  const correctedColor = c => ({ yes: 'badge-green', no: 'badge-red', pending: 'badge-amber' }[c] || 'badge-gray');

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Assignments</h1>
          <p className="page-subtitle">Track per-topic assignment submission and correction status</p>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:'wrap'}}><button className="btn btn-ghost btn-sm" onClick={exportList}>⬇ Export</button>{canEditA && !batchRestricted && <ImportButton configKey="assignments" onImported={()=>loadAll()}/>}{canEditA && (
          <>
            <button className="btn btn-secondary" onClick={() => setShowBulkEdit(true)}>✏️ Bulk Edit</button>
              <button className={`btn btn-sm ${showReport?'btn-primary':'btn-ghost'}`} onClick={()=>setShowReport(r=>!r)}>
                📊 {showReport?'Hide Report':'Report'}
              </button>
            <button className="btn btn-primary" onClick={openAdd}>
              <Plus size={14} /> Add Assignment
            </button>
            <button className="btn btn-secondary" onClick={()=>{ setBulkBatch(''); setBulkSelectedStudents([]); setShowBulkModal(true); }}>
              <Plus size={14} /> Bulk Add
            </button>
          </>
        )}</div>
      </div>

      <div className="stats-grid" style={{ marginBottom: 20 }}>
        {(() => {
          const t = localDate();
          const clear = () => { setFilterStatus(''); setFilterResub(false); setFilterOverdue(false); };
          const cards = [
            { label: 'Total', value: assignments.length, color: 'var(--text)', active: !filterStatus && !filterResub && !filterOverdue, go: clear },
            { label: 'Pending', value: assignments.filter(a => a.corrected === 'pending').length, color: 'var(--amber)', active: filterStatus === 'pending' && !filterOverdue, go: () => { clear(); if (filterStatus !== 'pending') setFilterStatus('pending'); } },
            { label: 'Corrected', value: assignments.filter(a => a.corrected === 'yes').length, color: 'var(--green)', active: filterStatus === 'yes', go: () => { clear(); if (filterStatus !== 'yes') setFilterStatus('yes'); } },
            { label: 'Overdue', value: assignments.filter(a => a.due_date && a.corrected === 'pending' && a.due_date < t).length, color: 'var(--red)', active: filterOverdue, go: () => { const was = filterOverdue; clear(); if (!was) setFilterOverdue(true); } },
            { label: 'Resubmission', value: assignments.filter(a => a.resubmission_required).length, color: 'var(--red)', active: filterResub, go: () => { const was = filterResub; clear(); if (!was) setFilterResub(true); } },
          ];
          return cards.map(c => (
            <div className="stat-card" key={c.label} style={{ cursor: 'pointer', border: c.active && c.label !== 'Total' ? '2px solid var(--accent)' : '2px solid transparent' }} onClick={c.go}>
              <div className="stat-label">{c.label}</div>
              <div className="stat-value" style={{ color: c.color, fontSize: 24 }}>{c.value}</div>
              {c.label !== 'Total' && <div style={{ fontSize: 9, color: 'var(--accent)', marginTop: 2 }}>{c.active ? '✕ Clear' : 'Click to filter'}</div>}
            </div>
          ));
        })()}
      </div>


      {/* Assignment Report */}
      {showReport && (
        <div className="card" style={{marginTop:16}}>
          <div style={{fontWeight:700,fontSize:14,marginBottom:12}}>📊 Assignment Report {filterBatch?`— ${filterBatch}`:''}</div>
          {(() => {
            const today = localDate();
            const allBatches = filterBatch
              ? [filterBatch]
              : orderBatchNames([...new Set(assignments.map(a=>a.students?.batch_name||a.students?.batches?.batch_name).filter(Boolean))]);
            // only topics that belong to this batch's discipline (a civil topic must not show as "not submitted" for a mechanical batch)
            const bDisc = getBatchDiscipline(filterBatch);
            const topicDisc = {}; topics.forEach(t=>{ topicDisc[t.topic_name]=(t.discipline||'').toLowerCase(); });
            const allTopics = [...new Set(assignments.map(a=>a.topic_name).filter(Boolean))].filter(t=>!bDisc||!topicDisc[t]||topicDisc[t]===bDisc).sort();

            if (!filterBatch) {
              // Batch summary
              return (
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                    <thead>
                      <tr style={{background:'var(--accent-light)'}}>
                        {['Batch','Total Records','✅ Corrected','⏳ Pending Correction','🚨 Overdue','Avg Score','Resubmission'].map(h=>(
                          <th key={h} style={{padding:'6px 10px',textAlign:'left',whiteSpace:'nowrap'}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {allBatches.map(batch=>{
                        const batchA = assignments.filter(a=>(a.students?.batch_name||a.students?.batches?.batch_name)===batch);
                        const corrected = batchA.filter(a=>a.corrected==='yes').length;
                        const pending = batchA.filter(a=>a.corrected==='pending').length;
                        const overdue = batchA.filter(a=>a.due_date&&a.corrected==='pending'&&a.due_date<today).length;
                        const resubmit = batchA.filter(a=>a.resubmission_required).length;
                        const scores = batchA.filter(a=>a.score&&Number(a.score)>0).map(a=>Number(a.score));
                        const avg = scores.length>0?(scores.reduce((s,v)=>s+v,0)/scores.length).toFixed(1):'—';
                        return (
                          <tr key={batch} style={{borderBottom:'1px solid var(--border)'}}>
                            <td style={{padding:'8px 10px',fontWeight:600}}>{batch}</td>
                            <td style={{padding:'8px 10px',textAlign:'center'}}>{batchA.length}</td>
                            <td style={{padding:'8px 10px',textAlign:'center',color:'var(--green)',fontWeight:600}}>{corrected}</td>
                            <td style={{padding:'8px 10px',textAlign:'center',color:pending>0?'var(--amber)':'var(--text3)'}}>{pending||'—'}</td>
                            <td style={{padding:'8px 10px',textAlign:'center',color:overdue>0?'var(--red)':'var(--text3)',fontWeight:overdue>0?700:400}}>{overdue||'—'}</td>
                            <td style={{padding:'8px 10px',textAlign:'center'}}>{avg}</td>
                            <td style={{padding:'8px 10px',textAlign:'center',color:resubmit>0?'var(--red)':'var(--text3)'}}>{resubmit||'—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            }

            // Topic-wise summary for selected batch
            const batchStudents = students.filter(s=>s.batch_name===filterBatch&&activeStudent(s));
            return (
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead>
                    <tr style={{background:'var(--accent-light)'}}>
                      {['Topic','Total Students','Submitted','Not Submitted','Pending Correction','Overdue','Avg Score'].map(h=>(
                        <th key={h} style={{padding:'6px 10px',textAlign:'left',whiteSpace:'nowrap'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {allTopics.map(topic=>{
                      const topicA = assignments.filter(a=>(a.students?.batch_name||a.students?.batches?.batch_name)===filterBatch&&a.topic_name===topic);
                      const submitted = new Set(topicA.map(a=>a.student_id)).size;
                      const notSub = batchStudents.length - submitted;
                      const pending = topicA.filter(a=>a.corrected==='pending').length;
                      const overdue = topicA.filter(a=>a.due_date&&a.corrected==='pending'&&a.due_date<today).length;
                      const scores = topicA.filter(a=>a.score).map(a=>Number(a.score));
                      const avg = scores.length>0?(scores.reduce((s,v)=>s+v,0)/scores.length).toFixed(1):'—';
                      return (
                        <tr key={topic} style={{borderBottom:'1px solid var(--border)'}}>
                          <td style={{padding:'8px 10px',fontWeight:500}}>{topic}</td>
                          <td style={{padding:'8px 10px',textAlign:'center'}}>{batchStudents.length}</td>
                          <td style={{padding:'8px 10px',textAlign:'center',color:'var(--green)',fontWeight:600}}>{submitted}</td>
                          <td style={{padding:'8px 10px',textAlign:'center',color:notSub>0?'var(--red)':'var(--text3)'}}>{notSub||'—'}</td>
                          <td style={{padding:'8px 10px',textAlign:'center',color:'var(--amber)'}}>{pending||'—'}</td>
                          <td style={{padding:'8px 10px',textAlign:'center',color:overdue>0?'var(--red)':'var(--text3)',fontWeight:overdue>0?700:400}}>{overdue||'—'}</td>
                          <td style={{padding:'8px 10px',textAlign:'center'}}>{avg}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="search-bar">
          <Search />
          <input placeholder="Search student, topic, reg no..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <SearchableSelect options={batches.map(b=>({value:b.batch_name,label:b.batch_name}))} value={filterBatch} onChange={v => { setFilterBatch(v); setFilterTopic(''); }} placeholder="All Batches" searchPlaceholder="Search batch..." style={{ minWidth: 130, maxWidth: 150 }} />
        <SearchableSelect options={(()=>{ const pfx=filterBatch?filterBatch.substring(0,2).toUpperCase():''; const disc=['EM','TM'].includes(pfx)?'mechanical':['EC','TC'].includes(pfx)?'civil':null; return (disc?topics.filter(t=>t.discipline===disc):topics).map(t=>({value:t.topic_name,label:t.short_code?`${t.short_code} — ${t.topic_name}`:t.topic_name})); })()} value={filterTopic} onChange={v => setFilterTopic(v)} placeholder="All Topics" searchPlaceholder="Search topic..." style={{ minWidth: 130, maxWidth: 150 }} />
        <select style={{ width: 120 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="yes">Corrected</option>
          <option value="no">Not Corrected</option>
        </select>
        <select style={{ width: 130 }} value={filterFaculty} onChange={e => setFilterFaculty(e.target.value)}>
          <option value="">All Faculty</option>
          {(facultyList.length > 0 ? facultyList.map(f => ({ id: f.id, name: f.faculty_name }))
            : [...new Map(assignments.filter(a => a.faculties?.faculty_name).map(a => [a.faculty_id, { id: a.faculty_id, name: a.faculties.faculty_name }])).values()]
          ).map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        {(search || filterBatch || filterTopic || filterStatus || filterFaculty || filterResub || filterOverdue) && (
          <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setFilterBatch(''); setFilterTopic(''); setFilterStatus(''); setFilterFaculty(''); setFilterResub(false); setFilterOverdue(false); }}>✕ Clear</button>
        )}
      </div>

      {/* Tab Bar */}
      <div style={{display:'flex',gap:0,marginBottom:16,borderBottom:'2px solid #e5e7eb'}}>
        <button onClick={()=>setActiveTab('assignments')} style={{padding:'8px 16px',border:'none',borderBottom:activeTab==='assignments'?'2px solid var(--accent)':'2px solid transparent',background:'none',fontWeight:activeTab==='assignments'?700:400,color:activeTab==='assignments'?'var(--accent)':'var(--text2)',cursor:'pointer',marginBottom:-2}}>📋 Assignments</button>
        <button onClick={()=>setActiveTab('notsubmitted')} style={{padding:'8px 16px',border:'none',borderBottom:activeTab==='notsubmitted'?'2px solid var(--accent)':'2px solid transparent',background:'none',fontWeight:activeTab==='notsubmitted'?700:400,color:activeTab==='notsubmitted'?'var(--accent)':'var(--text2)',cursor:'pointer',marginBottom:-2}}>⚠️ Not Submitted</button>
      </div>
      {activeTab==='notsubmitted' && (
        <div>
          <div style={{display:'flex',gap:8,marginBottom:12,flexWrap:'wrap'}}>
            <SearchableSelect options={batches.map(b=>({value:b.batch_name,label:b.batch_name}))} value={nsFilterBatch} onChange={v=>setNsFilterBatch(v)} placeholder="Select Batch" searchPlaceholder="Search batch..." style={{minWidth:150}}/>
            <SearchableSelect options={[...new Set(assignments.filter(a=>!nsFilterBatch||(a.students?.batch_name||a.students?.batches?.batch_name)===nsFilterBatch).map(a=>a.topic_name).filter(Boolean))].sort().map(t=>({value:t,label:t}))} value={nsFilterTopic} onChange={v=>setNsFilterTopic(v)} placeholder="Select Topic" searchPlaceholder="Search topic..." style={{minWidth:150}}/>
          </div>
          {nsFilterBatch && nsFilterTopic
            ? <NotSubmittedPanel show={true} onToggle={()=>{}} filterBatch={nsFilterBatch} filterTopic={nsFilterTopic} assignments={assignments} students={students} hideToggle={true} />
            : <div style={{padding:'24px',textAlign:'center',color:'var(--text3)'}}>Select a batch and topic to see not submitted students</div>}
        </div>
      )}
      {activeTab==='assignments' && filterBatch && filterTopic && <NotSubmittedPanel show={showNotSubmitted} onToggle={() => setShowNotSubmitted(v => !v)} filterBatch={filterBatch} filterTopic={filterTopic} assignments={assignments} students={students} />}
      {loading && <div className="loading"><div className="spinner" /></div>}
      {!loading && <div className="assign-wrap">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Student</th><th>Topic</th><th>Assignment Title</th>
                <th>Due Date</th><th>Submitted</th><th>Status</th><th>Score</th><th>Returned Date</th>
                <th>Resubmit?</th><th>Faculty</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={11}>
                  <div className="empty-state"><ClipboardList /><h3>No assignments yet</h3></div>
                </td></tr>
              ) : filtered.map(a => {
                  const today = localDate();
                  const isOverdue = a.due_date && a.corrected==='pending' && a.due_date < today;
                  const isDueSoon = a.due_date && a.corrected==='pending' && a.due_date >= today && a.due_date <= localDate(new Date(Date.now()+3*86400000));
                  return (
                <tr key={a.id} style={{background:isOverdue?'rgba(239,68,68,0.05)':isDueSoon?'rgba(245,158,11,0.05)':undefined}}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{a.students?.full_name}</div>
                    {a.updated_by&&<div style={{fontSize:9,color:'var(--text3)'}}>✏️ {a.updated_by} · {a.updated_at?new Date(a.updated_at).toLocaleDateString('en-IN'):''}</div>}
                    <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'monospace' }}>{a.students?.register_number}</div>
                    {a.students?.course_name && <div style={{ fontSize: 10, color: 'var(--accent)', marginTop: 2 }}>{a.students.course_name}</div>}
                  </td>
                  <td style={{ fontWeight: 500 }}>{a.topic_name}</td>
                  <td style={{ fontSize: 12, color: 'var(--text2)' }}>{a.assignment_title || '—'}</td>
                  <td style={{ fontSize: 12 }}>
                    {a.due_date ? (
                      <div>
                        <span style={{color: isOverdue?'var(--red)':isDueSoon?'var(--amber)':'var(--text)'}}>{fmtDMY(a.due_date)}</span>
                        {isOverdue&&<div style={{fontSize:10,color:'var(--red)',fontWeight:700}}>🚨 Overdue</div>}
                        {isDueSoon&&<div style={{fontSize:10,color:'var(--amber)',fontWeight:700}}>⚠️ Due soon</div>}
                      </div>
                    ) : '—'}
                  </td>
                  <td style={{ fontSize: 12 }}>{fmtDMY(a.submitted_date) || '—'}</td>
                  <td><span className={`badge ${correctedColor(a.corrected)}`}>{a.corrected}</span></td>
                  <td style={{ fontSize: 12, fontWeight: 600 }}>{a.score != null && a.score !== '' ? a.score : '—'}</td>
                  <td style={{ fontSize: 12 }}>{fmtDMY(a.returned_date) || '—'}</td>
                  <td>
                    <span className={`badge ${a.resubmission_required ? 'badge-red' : 'badge-gray'}`}>
                      {a.resubmission_required ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td style={{ fontSize: 12 }}>{facultyList.find(f => f.id === a.faculty_id)?.faculty_name || '—'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => setShowDetail(a)} title="View"><Eye size={13} /></button>
                      {canEditRow(a.students?.batch_name) && <button className="btn btn-ghost btn-sm" onClick={() => openEdit(a)} title="Edit"><Edit2 size={13} /></button>}
                      {a.corrected==='pending' && !a.submitted_date && <button className="btn btn-ghost btn-sm" style={{color:'#25d366'}} onClick={() => remindAssignment(a)} title="WhatsApp reminder">💬</button>}
                      {canEditRow(a.students?.batch_name) && <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} onClick={() => deleteAssignment(a.id)} title="Delete"><Trash2 size={13} /></button>}
                      {canEditRow(a.students?.batch_name) && a.corrected === 'pending' && (
                        <button className="btn btn-success btn-sm" onClick={() => updateCorrected(a.id, 'yes')}>✓ Correct</button>
                      )}
                    </div>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      </div>}

      {/* VIEW MODAL */}
      {showDetail && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowDetail(null)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">Assignment Details</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowDetail(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              {[
                ['Student', showDetail.students?.full_name + ' (' + showDetail.students?.register_number + ')'],
                ['Topic', showDetail.topic_name],
                ['Assignment Title', showDetail.assignment_title],
                ['Due Date', showDetail.due_date],
                ['Submitted Date', showDetail.submitted_date],
                ['Correction Status', showDetail.corrected],
                ['Returned Date', showDetail.returned_date],
                ['Score', showDetail.score != null && showDetail.score !== '' ? String(showDetail.score) : null],
                ['Resubmission Required', showDetail.resubmission_required ? 'Yes' : 'No'],
                ['Faculty', facultyList.find(f => f.id === showDetail.faculty_id)?.faculty_name],
                ['Faculty Remarks', showDetail.faculty_remarks],
                ['Feedback to Student', showDetail.feedback],
                ['Submission Link', showDetail.submission_link ? <a href={showDetail.submission_link} target="_blank" rel="noreferrer">Open submission ↗</a> : null],
              ].map(([label, val]) => val && (
                <div key={label} className="detail-row">
                  <div className="detail-label">{label}</div>
                  <div className="detail-value">{val}</div>
                </div>
              ))}
              {(() => {
                const hist = studentHistory(showDetail.student_id);
                if (hist.length < 2) return null;
                const scored = hist.filter(x => x.score != null && x.score !== '');
                const avg = scored.length ? (scored.reduce((t, x) => t + Number(x.score), 0) / scored.length).toFixed(1) : '—';
                return (
                  <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <strong style={{ fontSize: 13 }}>All assignments of this student ({hist.length}) · Corrected {hist.filter(x => x.corrected === 'yes').length} · Pending {hist.filter(x => x.corrected === 'pending').length} · Avg score {avg}</strong>
                      <button className="btn btn-ghost btn-sm" onClick={() => exportStudentHistory(showDetail.student_id)}>⬇ Excel</button>
                    </div>
                    <div style={{ maxHeight: 180, overflowY: 'auto', fontSize: 12 }}>
                      {hist.map(x => (
                        <div key={x.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid var(--border)', cursor: 'pointer', fontWeight: x.id === showDetail.id ? 700 : 400 }} onClick={() => setShowDetail(x)}>
                          <span>{x.topic_name}{x.assignment_title ? ' — ' + x.assignment_title : ''}</span>
                          <span>{x.corrected}{x.score != null && x.score !== '' ? ' · ' + x.score : ''}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="modal-footer">
              {canEditRow(showDetail.students?.batch_name) && <button className="btn btn-secondary" onClick={() => { setShowDetail(null); openEdit(showDetail); }}><Edit2 size={13} /> Edit</button>}
              <button className="btn btn-primary" onClick={() => setShowDetail(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* BULK ADD MODAL */}
      {showBulkModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowBulkModal(false)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <span className="modal-title">Bulk Add Assignment</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowBulkModal(false)}><X size={16}/></button>
            </div>
            <div className="modal-body">
              {/* Batch + student selection */}
              <div style={{marginBottom:12}}>
                <label className="form-label">Batch *</label>
                <select className="form-control" style={{width:200}} value={bulkBatch} onChange={e=>{
                  setBulkBatch(e.target.value);
                  setBulkSelectedStudents(students.filter(s=>activeStudent(s)&&s.batch_name===e.target.value).map(s=>s.id)); setBulkStudentSearch('');
                }}>
                  <option value="">Select Batch</option>
                  {editableBatches.map(b=><option key={b.id} value={b.batch_name}>{b.batch_name}</option>)}
                </select>
              </div>
              {bulkBatch && (
                <div style={{marginBottom:12}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                    <label className="form-label">Students ({bulkSelectedStudents.length}/{students.filter(s=>activeStudent(s)&&s.batch_name===bulkBatch).length} selected)</label>
                    <div style={{display:'flex',gap:6}}>
                      <button className="btn btn-ghost btn-sm" onClick={()=>setBulkSelectedStudents(students.filter(s=>activeStudent(s)&&s.batch_name===bulkBatch).map(s=>s.id))}>Select All</button>
                      <button className="btn btn-ghost btn-sm" onClick={()=>setBulkSelectedStudents([])}>Clear</button>
                    </div>
                  </div>
                  <input type="text" placeholder="Search student..." value={bulkStudentSearch}
                    onChange={e=>setBulkStudentSearch(e.target.value)}
                    style={{width:'100%',marginBottom:6,padding:'4px 8px',fontSize:12,borderRadius:6,border:'1px solid var(--border)'}}/>
                  <div style={{maxHeight:180,overflowY:'auto',border:'1px solid var(--border)',borderRadius:6,padding:8,display:'flex',flexWrap:'wrap',gap:4}}>
                    {students.filter(s=>activeStudent(s)&&s.batch_name===bulkBatch&&(!bulkStudentSearch||s.full_name.toLowerCase().includes(bulkStudentSearch.toLowerCase())||s.register_number?.toLowerCase().includes(bulkStudentSearch.toLowerCase()))).map(s=>{
                      const sel = bulkSelectedStudents.includes(s.id);
                      return <button key={s.id} className={`btn btn-sm ${sel?'btn-primary':'btn-secondary'}`} style={{fontSize:11}}
                        onClick={()=>setBulkSelectedStudents(prev=>sel?prev.filter(x=>x!==s.id):[...prev,s.id])}>
                        {sel?'✓ ':''}{s.full_name}
                      </button>;
                    })}
                  </div>
                </div>
              )}
              {/* Common assignment details */}
              <div className="form-grid">
                <div className="form-group">
                  <label>Topic *</label>
                  <select value={bulkForm.topic_name} onChange={e=>setBulkForm(f=>({...f,topic_name:e.target.value}))}>
                    <option value="">Select Topic</option>
                    {(()=>{
                      const pfx = bulkBatch ? bulkBatch.substring(0,2).toUpperCase() : '';
                      const disc = ['EM','TM'].includes(pfx) ? 'mechanical' : ['EC','TC'].includes(pfx) ? 'civil' : null;
                      const filtered = disc ? topics.filter(t=>t.discipline===disc) : topics;
                      return filtered.map(t=><option key={t.id} value={t.topic_name}>{t.short_code?`${t.short_code} - ${t.topic_name}`:t.topic_name}</option>);
                    })()}
                  </select>
                </div>
                <div className="form-group">
                  <label>Assignment Title</label>
                  <input type="text" value={bulkForm.assignment_title} onChange={e=>setBulkForm(f=>({...f,assignment_title:e.target.value}))} placeholder="Assignment title"/>
                </div>
                <div className="form-group">
                  <label>Submitted Date</label>
                  <DateInput value={bulkForm.submitted_date} onChange={e=>setBulkForm(f=>({...f,submitted_date:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select value={bulkForm.corrected} onChange={e=>setBulkForm(f=>({...f,corrected:e.target.value}))}>
                    <option value="pending">Pending</option>
                    <option value="yes">Corrected</option>
                    <option value="no">Not Corrected</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Due Date</label>
                  <DateInput value={bulkForm.due_date} onChange={e=>setBulkForm(f=>({...f,due_date:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Score (same for all, optional)</label>
                  <input type="number" min="0" value={bulkForm.score} onChange={e=>setBulkForm(f=>({...f,score:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Faculty Remarks</label>
                  <input type="text" value={bulkForm.faculty_remarks} onChange={e=>setBulkForm(f=>({...f,faculty_remarks:e.target.value}))} placeholder="Remarks"/>
                </div>
                <div className="form-group">
                  <label>Returned Date</label>
                  <DateInput value={bulkForm.returned_date} onChange={e=>setBulkForm(f=>({...f,returned_date:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Resubmission Required</label>
                  <select value={bulkForm.resubmission_required?'yes':'no'} onChange={e=>setBulkForm(f=>({...f,resubmission_required:e.target.value==='yes'}))}>
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>
                {bulkForm.resubmission_required && (
                  <div className="form-group">
                    <label>Resubmission Date</label>
                    <DateInput value={bulkForm.resubmission_date} onChange={e=>setBulkForm(f=>({...f,resubmission_date:e.target.value}))}/>
                  </div>
                )}
                <div className="form-group">
                  <label>Faculty</label>
                  <select value={bulkForm.faculty_id} onChange={e=>setBulkForm(f=>({...f,faculty_id:e.target.value}))}>
                    <option value="">Select Faculty</option>
                    {facultyList.map(f=><option key={f.id} value={f.id}>{f.faculty_name}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>setShowBulkModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveBulkAssignments} disabled={bulkSaving||!bulkBatch||!bulkForm.topic_name||!bulkSelectedStudents.length}>
                {bulkSaving?'Saving...':'Add for '+bulkSelectedStudents.length+' Students'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* BULK EDIT MODAL */}
      {showBulkEdit && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowBulkEdit(false)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <span className="modal-title">Bulk Edit Assignments</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowBulkEdit(false)}><X size={16}/></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Batch</label>
                  <SearchableSelect options={editableBatches.map(b=>({value:b.batch_name,label:b.batch_name}))} value={bulkEditBatch} onChange={v=>{setBulkEditBatch(v);setBulkEditSelected([]);}} placeholder="Select Batch"/>
                </div>
                <div className="form-group">
                  <label>Topic</label>
                  <SearchableSelect options={[...new Set(assignments.filter(a=>!bulkEditBatch||(a.students?.batch_name||'')===bulkEditBatch).map(a=>a.topic_name).filter(Boolean))].sort().map(t=>({value:t,label:t}))} value={bulkEditTopic} onChange={v=>{setBulkEditTopic(v);setBulkEditSelected([]);}} placeholder="Select Topic"/>
                </div>
              </div>
              <div className="form-group">
                <label>Action</label>
                <div style={{display:'flex',gap:8}}>
                  <button className={bulkEditAction==='status'?'btn btn-primary':'btn btn-secondary'} onClick={()=>setBulkEditAction('status')}>Update Status</button>
                  <button className={bulkEditAction==='faculty'?'btn btn-primary':'btn btn-secondary'} onClick={()=>setBulkEditAction('faculty')}>Assign Faculty</button>
                  <button className={bulkEditAction==='submitted'?'btn btn-primary':'btn btn-secondary'} onClick={()=>setBulkEditAction('submitted')}>Mark Submitted</button>
                  <button className={bulkEditAction==='due'?'btn btn-primary':'btn btn-secondary'} onClick={()=>setBulkEditAction('due')}>Set Due Date</button>
                  <button className={bulkEditAction==='scores'?'btn btn-primary':'btn btn-secondary'} onClick={()=>setBulkEditAction('scores')}>Enter Scores</button>
                </div>
              </div>
              {bulkEditAction==='status' && (
                <div className="form-group">
                  <label>Status</label>
                  <select value={bulkEditStatus} onChange={e=>setBulkEditStatus(e.target.value)}>
                    <option value="yes">✅ Corrected</option>
                    <option value="pending">⏳ Pending</option>
                    <option value="no">❌ Not Corrected</option>
                  </select>
                </div>
              )}
              {bulkEditAction==='due' && (
                <div className="form-group">
                  <label>Due Date</label>
                  <DateInput value={bulkEditDue} onChange={e=>setBulkEditDue(e.target.value)}/>
                </div>
              )}
              {bulkEditAction==='scores' && (
                <label style={{display:'flex',gap:6,alignItems:'center',fontSize:12,marginBottom:8}}>
                  <input type="checkbox" checked={bulkMarkCorrected} onChange={e=>setBulkMarkCorrected(e.target.checked)}/>
                  Also mark as Corrected (and set returned date to today) for the rows I enter a score for
                </label>
              )}
              {bulkEditAction==='faculty' && (
                <div className="form-group">
                  <label>Faculty</label>
                  <SearchableSelect options={facultyList.map(f=>({value:f.id,label:f.faculty_name}))} value={bulkEditFaculty} onChange={v=>setBulkEditFaculty(v)} placeholder="Select Faculty"/>
                </div>
              )}
              {bulkEditBatch && bulkEditTopic && (
                <div>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                    <label style={{fontWeight:600}}>Select Assignments ({bulkEditSelected.length} selected)</label>
                    <button className="btn btn-sm btn-ghost" onClick={()=>{const ids=assignments.filter(a=>(a.students?.batch_name||'')===bulkEditBatch&&a.topic_name===bulkEditTopic).map(a=>a.id);setBulkEditSelected(bulkEditSelected.length===ids.length?[]:ids);}}>
                      {bulkEditSelected.length===assignments.filter(a=>(a.students?.batch_name||'')===bulkEditBatch&&a.topic_name===bulkEditTopic).length?'Deselect All':'Select All'}
                    </button>
                  </div>
                  <input placeholder="🔍 Search student..." value={bulkEditSearch}
                    onChange={e=>setBulkEditSearch(e.target.value)}
                    style={{width:'100%',padding:'6px 10px',borderRadius:6,border:'1px solid var(--border)',fontSize:13,marginBottom:8}}/>
                  <div style={{maxHeight:300,overflowY:'auto',border:'1px solid #e5e7eb',borderRadius:6}}>
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                      <thead style={{position:'sticky',top:0,background:'var(--accent-light)',zIndex:1}}>
                        <tr>
                          <th style={{padding:'6px 10px',textAlign:'center',width:36}}>✓</th>
                          <th style={{padding:'6px 10px',textAlign:'left'}}>Reg No</th>
                          <th style={{padding:'6px 10px',textAlign:'left'}}>Name</th>
                          <th style={{padding:'6px 10px',textAlign:'center'}}>Status</th>
                          {bulkEditAction==='scores' && <th style={{padding:'6px 10px',textAlign:'center',width:90}}>Score</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {assignments.filter(a=>(a.students?.batch_name||'')===bulkEditBatch&&a.topic_name===bulkEditTopic&&
                          (!bulkEditSearch||a.students?.full_name?.toLowerCase().includes(bulkEditSearch.toLowerCase())||a.students?.register_number?.toLowerCase().includes(bulkEditSearch.toLowerCase()))
                        ).sort((a,b)=>(a.students?.register_number||'').localeCompare(b.students?.register_number||'',undefined,{numeric:true})).map(a=>(
                          <tr key={a.id} style={{borderBottom:'1px solid #f3f4f6',cursor:'pointer',background:bulkEditSelected.includes(a.id)?'#eff6ff':'',}} onClick={()=>setBulkEditSelected(bulkEditSelected.includes(a.id)?bulkEditSelected.filter(id=>id!==a.id):[...bulkEditSelected,a.id])}>
                            <td style={{textAlign:'center',padding:'6px 10px'}}>
                              <input type="checkbox" checked={bulkEditSelected.includes(a.id)} onChange={()=>{}} onClick={e=>e.stopPropagation()}/>
                            </td>
                            <td style={{padding:'6px 10px',fontFamily:'monospace',fontSize:12,color:'var(--text3)'}}>{a.students?.register_number}</td>
                            <td style={{padding:'6px 10px',fontWeight:500}}>{a.students?.full_name}</td>
                            <td style={{padding:'6px 10px',textAlign:'center'}}>
                              <span className={`badge ${a.corrected==='yes'?'badge-green':a.corrected==='pending'?'badge-yellow':'badge-gray'}`}>{a.corrected}</span>
                            </td>
                            {bulkEditAction==='scores' && (
                              <td style={{padding:'4px 10px',textAlign:'center'}} onClick={e=>e.stopPropagation()}>
                                <input type="number" min="0" style={{width:70,textAlign:'center'}} value={bulkScores[a.id] ?? (a.score ?? '')}
                                  onChange={e=>setBulkScores(prev=>({...prev,[a.id]:e.target.value}))}/>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setShowBulkEdit(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={bulkEditSaving||(bulkEditAction==='scores'?Object.values(bulkScores).filter(v=>v!=='').length===0:bulkEditSelected.length===0)} onClick={applyBulkEdit}>
                {bulkEditSaving?'Saving...':bulkEditAction==='scores'?'Save '+Object.values(bulkScores).filter(v=>v!=='').length+' scores':'Apply to '+bulkEditSelected.length+' assignments'}</button>
            </div>
          </div>
        </div>
      )}
      {/* ADD / EDIT MODAL */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{editing ? 'Edit Assignment' : 'Record Assignment'}</span>
              <button className="btn btn-ghost btn-sm" onClick={closeModal}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">

                {/* Batch filter */}
                <div className="form-group">
                  <label>Filter by Batch</label>
                  <SearchableSelect
                    options={orderBatchNames([...new Set([...batches.map(b=>b.batch_name),...students.map(s=>s.batch_name)].filter(Boolean))].filter(ownBatch)).map(n=>({value:n,label:n}))}
                    value={modalBatch}
                    onChange={v => { setModalBatch(v); setForm(f => ({ ...f, student_id: '' })); }}
                    placeholder="All Batches"
                    searchPlaceholder="Search batch..."
                  />
                </div>

                {/* Student — searchable */}
                <div className="form-group">
                  <label>Student *</label>
                  <SearchableSelect
                    options={(modalBatch ? students.filter(s => s.batch_name===modalBatch).sort(byRegNo) : students).filter(s => ownBatch(s.batch_name)).map(s => ({ value: s.id, label: `${s.full_name} (${s.register_number})` }))}
                    value={form.student_id}
                    onChange={v => {
                      // Auto-generate assignment title
                      const existingCount = assignments.filter(a => a.student_id === v).length;
                      const nextTitle = `Assignment ${existingCount + 1}`;
                      setForm(f => ({ ...f, student_id: v, assignment_title: f.assignment_title || nextTitle }));
                    }}
                    placeholder="Select Student"
                    searchPlaceholder="Search student..."
                  />
                </div>

                {/* Topic */}
                <div className="form-group">
                  <label>Topic Name *</label>
                  <SearchableSelect
                    options={(()=>{const disc=getBatchDiscipline(modalBatch);return (disc?topics.filter(t=>!t.discipline||t.discipline.toLowerCase()===disc):topics);})().map(t=>({value:t.topic_name,label:t.short_code?`${t.short_code} — ${t.topic_name}`:t.topic_name}))}
                    value={form.topic_name||''}
                    onChange={v=>setForm(f=>({...f,topic_name:v}))}
                    placeholder="Select Topic..."
                    searchPlaceholder="Search topic..."
                  />
                </div>

                <div className="form-group">
                  <label>Assignment Title</label>
                  <input value={form.assignment_title} onChange={e => setForm(f => ({ ...f, assignment_title: e.target.value }))} placeholder="e.g. Assignment 1" />
                </div>

                <div className="form-group">
                  <label>Due Date</label>
                  <DateInput value={form.due_date||''} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} />
                </div>

                <div className="form-group">
                  <label>Submitted Date</label>
                  <DateInput value={form.submitted_date} onChange={e => setForm(f => ({ ...f, submitted_date: e.target.value }))} />
                </div>

                <div className="form-group">
                  <label>Correction Status</label>
                  <select value={form.corrected} onChange={e => setForm(f => ({ ...f, corrected: e.target.value }))}>
                    <option value="pending">Pending</option>
                    <option value="yes">Corrected</option>
                    <option value="no">Not Corrected</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Returned Date</label>
                  <DateInput value={form.returned_date} onChange={e => setForm(f => ({ ...f, returned_date: e.target.value }))} />
                </div>

                <div className="form-group">
                  <label>Score</label>
                  <input type="number" min="0" step="0.5" value={form.score} onChange={e => setForm(f => ({ ...f, score: e.target.value }))} placeholder="Marks / grade points" />
                </div>

                <div className="form-group">
                  <label>Submission Link (Drive / file URL, optional)</label>
                  <input type="url" value={form.submission_link||''} onChange={e => setForm(f => ({ ...f, submission_link: e.target.value }))} placeholder="https://drive.google.com/..." />
                </div>

                <div className="form-group">
                  <label>Resubmission Required?</label>
                  <select value={form.resubmission_required ? 'yes' : 'no'} onChange={e => setForm(f => ({ ...f, resubmission_required: e.target.value === 'yes' }))}>
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>

                {form.resubmission_required && (
                  <div className="form-group">
                    <label>Resubmission Date</label>
                    <DateInput value={form.resubmission_date} onChange={e => setForm(f => ({ ...f, resubmission_date: e.target.value }))} />
                  </div>
                )}

                <div className="form-group">
                  <label>Faculty</label>
                  <select value={form.faculty_id} onChange={e => setForm(f => ({ ...f, faculty_id: e.target.value }))}>
                    <option value="">Select Faculty</option>
                    {facultyList.map(f => <option key={f.id} value={f.id}>{f.faculty_name}</option>)}
                  </select>
                </div>

                <div className="form-group full">
                  <label>Faculty Remarks</label>
                  <textarea value={form.faculty_remarks} onChange={e => setForm(f => ({ ...f, faculty_remarks: e.target.value }))} placeholder="Optional remarks..." />
                </div>

                <div className="form-group full">
                  <label>Feedback / Comments to Student</label>
                  <textarea value={form.feedback||''} onChange={e => setForm(f => ({ ...f, feedback: e.target.value }))} placeholder="Detailed feedback for the student..." rows={3}/>
                </div>

              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Saving...' : editing ? 'Update Assignment' : 'Save Assignment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
